import React, { useCallback, useMemo, useState } from "react";
import Cropper from "react-easy-crop";

const ID_CARD_ASPECT = 85.6 / 53.98;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getRadianAngle = (degreeValue) =>
  (degreeValue * Math.PI) / 180;

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) +
      Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) +
      Math.abs(Math.cos(rotRad) * height),
  };
};

async function getCroppedImage(
  imageSrc,
  pixelCrop,
  rotation = 0,
  fileName = "cropped-id.jpg"
) {
  const image = await createImage(imageSrc);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas initialize nahi hua.");
  }

  const rotRad = getRadianAngle(rotation);

  const { width: boundingWidth, height: boundingHeight } =
    rotateSize(image.width, image.height, rotation);

  canvas.width = boundingWidth;
  canvas.height = boundingHeight;

  ctx.translate(boundingWidth / 2, boundingHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    throw new Error("Crop canvas initialize nahi hua.");
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  const blob = await new Promise((resolve) => {
    croppedCanvas.toBlob(
      (result) => resolve(result),
      "image/jpeg",
      0.92
    );
  });

  if (!blob) {
    throw new Error("Cropped image create nahi hui.");
  }

  return new File([blob], fileName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

function ImageCropModal({
  imageUrl,
  title = "Crop ID",
  originalFileName = "id.jpg",
  onCancel,
  onSave,
}) {
  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  });

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState(null);

  const [saving, setSaving] = useState(false);

  const outputName = useMemo(() => {
    const cleanName = String(originalFileName || "id")
      .replace(/\.[^.]+$/, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_");

    return `${cleanName}_cropped.jpg`;
  }, [originalFileName]);

  const onCropComplete = useCallback(
    (_, croppedPixels) => {
      setCroppedAreaPixels(croppedPixels);
    },
    []
  );

  const fitImage = () => {
    setCrop({
      x: 0,
      y: 0,
    });

    setZoom(1);
    setRotation(0);
  };

  const rotateLeft = () => {
    setRotation((current) => current - 90);
  };

  const rotateRight = () => {
    setRotation((current) => current + 90);
  };

  const handleSave = async () => {
    if (!croppedAreaPixels || saving) return;

    setSaving(true);

    try {
      const file = await getCroppedImage(
        imageUrl,
        croppedAreaPixels,
        rotation,
        outputName
      );

      await onSave(file);
    } catch (error) {
      console.error("ID crop error:", error);

      alert(
        "ID crop save nahi hui: " +
          (error?.message || error)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <strong>{title}</strong>

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={closeButton}
          >
            ×
          </button>
        </div>

        <div style={cropAreaStyle}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={ID_CARD_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </div>

        <div style={controlsStyle}>
          <label style={controlLabel}>
            <span>Zoom</span>

            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) =>
                setZoom(Number(event.target.value))
              }
              style={{ width: "100%" }}
            />
          </label>

          <label style={controlLabel}>
            <span>
              Rotate: {rotation % 360}°
            </span>

            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={(event) =>
                setRotation(Number(event.target.value))
              }
              style={{ width: "100%" }}
            />
          </label>

          <div style={smallButtons}>
            <button
              type="button"
              onClick={rotateLeft}
              style={smallButton}
            >
              ↶ Rotate Left
            </button>

            <button
              type="button"
              onClick={fitImage}
              style={smallButton}
            >
              ⛶ Auto Fit
            </button>

            <button
              type="button"
              onClick={rotateRight}
              style={smallButton}
            >
              ↷ Rotate Right
            </button>
          </div>
        </div>

        <div style={footerStyle}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={cancelButton}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={saveButton}
          >
            {saving
              ? "Saving..."
              : "Save Cropped Image"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 30000,
  background: "rgba(15,23,42,.82)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
};

const modalStyle = {
  color: "#0f172a",
  width: "min(720px,100%)",
  maxHeight: "96vh",
  overflowY: "auto",
  background: "#fff",
  borderRadius: 18,
  boxShadow: "0 30px 90px rgba(0,0,0,.4)",
};

const headerStyle = {
  color: "#0f172a",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "15px 18px",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 18,
};

const closeButton = {
  color: "#0f172a",
  border: 0,
  background: "#f1f5f9",
  width: 34,
  height: 34,
  borderRadius: 9,
  fontSize: 22,
  cursor: "pointer",
};

const cropAreaStyle = {
  height: "430px",
  position: "relative",
  background: "#111827",
};

const controlsStyle = {
  padding: 18,
  display: "grid",
  gap: 15,
};

const controlLabel = {
  color: "#0f172a",
  display: "grid",
  gap: 7,
  fontWeight: 700,
  fontSize: 13,
};

const smallButtons = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(130px,1fr))",
  gap: 8,
};

const smallButton = {
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: 9,
  padding: "9px 10px",
  background: "#f8fafc",
  cursor: "pointer",
  fontWeight: 700,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  padding: "14px 18px",
  borderTop: "1px solid #e2e8f0",
};

const cancelButton = {
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: 9,
  padding: "10px 15px",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};

const saveButton = {
  border: 0,
  borderRadius: 9,
  padding: "10px 15px",
  background: "#2563eb",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};

export default ImageCropModal;